import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import LoginScreen from './LoginScreen';
import HomePage from './pages/HomePage';
import SuggestPage from './pages/SuggestPage';
import BarDetailPage from './pages/BarDetailPage';
import { saveUserMetadata, getUserMetadata } from './helpers/api';
import { getFirstName } from './helpers/constants';
import './App.css';

function Header({ userId, userMeta, loadingUserMeta, onLogout }) {
  const displayName = getFirstName(userMeta?.name);

  console.log('Header render - userId:', userId, 'userMeta:', userMeta, 'loadingUserMeta:', loadingUserMeta, 'displayName:', displayName);

  return (
    <header className="app-header">
      <div className="header-content">
        <h1 className="app-title">
          <Link to="/">Bares do rolê 🍻</Link>
        </h1>

        <nav className="header-nav">
          <Link to="/sugerir" className="nav-link">
            Sugerir bar
          </Link>

          {userId && (
            <div className="user-menu">
              {loadingUserMeta ? (
                <span className="user-name" style={{ opacity: 0.5 }}>...</span>
              ) : (
                <span className="user-name">{displayName}</span>
              )}
              <button onClick={onLogout} className="btn-logout">
                Sair
              </button>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
}

function LoginPage({ userId, onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  // Redirect to home if already logged in
  useEffect(() => {
    if (userId) {
      console.log('User already logged in, redirecting to:', from);
      navigate(from, { replace: true });
    }
  }, [userId, navigate, from]);

  const handleLoginSuccess = (id, metadata) => {
    onLogin(id, metadata);
    navigate(from); // Redirect to where user came from
  };

  // Don't render login screen if already logged in
  if (userId) {
    return null;
  }

  return (
    <div className="login-page">
      <LoginScreen onLogin={handleLoginSuccess} />
    </div>
  );
}

function App() {
  const [userId, setUserId] = useState(null);
  const [userMeta, setUserMeta] = useState(null);
  const [loadingUserMeta, setLoadingUserMeta] = useState(false);
  const sessionLoadedRef = useRef(false); // Track if session has been loaded

  // Load user session from localStorage on mount
  useEffect(() => {
    // Prevent double loading in development mode
    if (sessionLoadedRef.current) {
      console.log('Session already loaded, skipping...');
      return;
    }

    console.log('Loading session...');
    sessionLoadedRef.current = true;

    let isMounted = true; // Track if component is still mounted

    async function loadSession() {
      try {
        const storedUserId = localStorage.getItem('userId');
        console.log('Stored userId:', storedUserId);

        if (!storedUserId) {
          console.log('No userId found in localStorage');
          return;
        }

        // Set userId first
        if (isMounted) {
          setUserId(storedUserId);
          console.log('Set userId:', storedUserId);
        }

        // Try to get metadata from localStorage
        const storedUserMeta = localStorage.getItem('userMeta');
        console.log('Stored userMeta raw:', storedUserMeta);

        let metadata = null;

        if (storedUserMeta) {
          try {
            metadata = JSON.parse(storedUserMeta);
            console.log('Parsed metadata:', metadata);
          } catch (err) {
            console.error('Error parsing stored metadata:', err);
          }
        }

        // If we have metadata with name, use it immediately
        if (metadata && metadata.name) {
          console.log('Using cached metadata with name:', metadata.name);
          if (isMounted) {
            setUserMeta(metadata);
            console.log('Set userMeta from cache');
          }
          return; // Don't fetch from backend
        }

        // Otherwise, fetch from backend
        console.log('No cached metadata with name, fetching from backend...');
        try {
          if (isMounted) setLoadingUserMeta(true);

          const response = await getUserMetadata(storedUserId);
          console.log('Backend response:', response);

          if (response?.metadata && isMounted) {
            // Update both state and localStorage
            setUserMeta(response.metadata);
            localStorage.setItem('userMeta', JSON.stringify(response.metadata));
            console.log('User metadata loaded from backend and saved:', response.metadata);
          }
        } catch (err) {
          console.error('Error fetching user metadata:', err);
        } finally {
          if (isMounted) setLoadingUserMeta(false);
        }
      } catch (err) {
        console.error('Error loading user session:', err);
      }
    }

    loadSession();

    return () => {
      isMounted = false; // Cleanup on unmount
    };
  }, []);

  const handleLogin = useCallback(async (id, metadata) => {
    console.log('handleLogin called - id:', id, 'metadata:', metadata);

    setUserId(id);

    // Persist userId to localStorage
    localStorage.setItem('userId', id);

    if (metadata && (metadata.name || metadata.email)) {
      // Save to backend if not guest (this will preserve existing fields)
      if (!id.startsWith('guest:')) {
        try {
          setLoadingUserMeta(true);

          console.log('Saving partial metadata to backend:', metadata);
          await saveUserMetadata(id, metadata);

          // Fetch complete metadata from backend (includes preserved name)
          console.log('Fetching complete metadata from backend...');
          const response = await getUserMetadata(id);
          if (response?.metadata) {
            console.log('Complete metadata from backend:', response.metadata);
            setUserMeta(response.metadata);
            localStorage.setItem('userMeta', JSON.stringify(response.metadata));
          } else {
            // Fallback to what we have
            setUserMeta(metadata);
            localStorage.setItem('userMeta', JSON.stringify(metadata));
          }
        } catch (err) {
          console.error('Error saving/fetching user metadata:', err);
          // Fallback to what we have
          setUserMeta(metadata);
          localStorage.setItem('userMeta', JSON.stringify(metadata));
        } finally {
          setLoadingUserMeta(false);
        }
      } else {
        // Guest user - just use what we have
        setUserMeta(metadata);
        localStorage.setItem('userMeta', JSON.stringify(metadata));
      }
    } else {
      // No metadata provided - preserve existing if any
      console.log('No metadata provided on login');
      const existingMeta = localStorage.getItem('userMeta');
      if (existingMeta) {
        try {
          const parsed = JSON.parse(existingMeta);
          console.log('Using existing metadata from localStorage:', parsed);
          setUserMeta(parsed);
        } catch (err) {
          console.error('Error parsing existing metadata:', err);
          setUserMeta(null);
        }
      } else {
        setUserMeta(null);
      }
    }
  }, []);

  const handleLogout = () => {
    setUserId(null);
    setUserMeta(null);
    setLoadingUserMeta(false);
    sessionLoadedRef.current = false; // Reset session loaded flag

    // Clear localStorage
    try {
      localStorage.removeItem('userId');
      localStorage.removeItem('userMeta');
    } catch (err) {
      console.error('Error clearing session from localStorage:', err);
    }
  };

  return (
    <div className="app">
      <Header userId={userId} userMeta={userMeta} loadingUserMeta={loadingUserMeta} onLogout={handleLogout} />

      <main className="app-main">
        <Routes>
          <Route path="/" element={<HomePage userId={userId} />} />
          <Route path="/login" element={<LoginPage userId={userId} onLogin={handleLogin} />} />
          <Route path="/sugerir" element={<SuggestPage userId={userId} />} />
          <Route path="/bar/:key" element={<BarDetailPage userId={userId} />} />
        </Routes>
      </main>
    </div>
  );
}

export default App;
